import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { cancelarPlan, obtenerPrestamo } from '../api/prestamosApi.js';
import { importe as formatearImporte } from '../formato.js';

const LARGO_MAXIMO_DEL_MOTIVO = 500;

/**
 * CANCELAR EL PLAN DE PAGOS (P-20-07, RF-271, D-440, RN-754).
 *
 * PARA QUÉ SE CANCELA: porque lo declarado del plan NO SE EDITA —monto original,
 * pago mensual, plazo, cuántos pagos iban, fecha del próximo pago y categoría—, y la
 * forma de corregirlo es cancelar y volver a generar. Es la misma regla con la que el
 * 19 cerró la corrección de un plan en curso (RN-720).
 *
 * QUÉ NO CAMBIA, Y ES LO QUE MÁS IMPORTA DECIR ANTES DE CONFIRMAR: no cambia lo que
 * debes, ni los pagos que ya registraste, ni sus gastos de intereses. Las filas se
 * CONSERVAN en cancelado, para que quede el rastro. Lo que deja de aparecer es el
 * calendario, el conteo y el renglón en lo que vence.
 *
 * NO EXISTE DESCANCELAR (RN-754): un plan cancelado no se reactiva, se genera de
 * nuevo. Y SIN CONFIRMACIÓN EXPLÍCITA NO SE ESCRIBE NADA: el servidor responde
 * pidiéndola junto con el efecto explicado, así que la salvaguarda no depende de que
 * esta pantalla se acuerde de preguntar.
 */
export default function CancelarPlanDePagos() {
  const { idCuenta } = useParams();
  const navegar = useNavigate();
  const hoy = obtenerFechaDeNegocioHoy();

  const [prestamo, setPrestamo] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState(null);
  const [cancelando, setCancelando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setPrestamo(await obtenerPrestamo({ idCuenta: Number(idCuenta), fechaLocal: hoy }));
      } catch (fallo) {
        setError(fallo);
      }
    })();
  }, [idCuenta, hoy]);

  const motivoSePasa = motivo.trim().length > LARGO_MAXIMO_DEL_MOTIVO;
  const procede = Boolean(prestamo?.puedeCancelarElPlan) && !motivoSePasa;

  async function confirmar() {
    if (!procede) return;
    setError(null);
    setCancelando(true);
    try {
      await cancelarPlan({
        idCuenta: Number(idCuenta),
        confirmado: true,
        motivo: motivo.trim() || null,
        fechaLocal: hoy,
        // RNF-013 · si el plan cambió mientras lo revisaba, el servidor responde
        // conflicto y no se pisa el cambio de nadie.
        version: prestamo?.versionDelPlan,
      });
      navegar(`/cuentas/prestamos/${idCuenta}?acuse=cancelacion`, { replace: true });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setCancelando(false);
    }
  }

  return (
    <Shell titulo="Cancelar el plan de pagos" volverA={`/cuentas/prestamos/${idCuenta}`}>
      <CintaDeVersionNueva />

      <AvisoDeError error={error} />

      {prestamo && (
        <>
          <div className="card" style={{ margin: 12 }} data-prueba="cancelar-plan-pregunta">
            <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>
              ¿CANCELAR EL PLAN DE PAGOS DE {prestamo.cuenta.nombre.toUpperCase()}?
            </h4>
            <span className="ayuda">
              El plan de pagos es el calendario de lo que te falta: con él, el préstamo aparece en lo
              que vence y lleva el conteo de cada pago. Se suele cancelar cuando algo de lo que
              declaraste cambió —el plazo, el pago mensual o la fecha de pago—, porque esos datos no
              se editan: se cancela y se genera de nuevo.
            </span>
          </div>

          <div className="card" style={{ margin: 12 }} data-prueba="cancelar-plan-efecto">
            <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>QUÉ CAMBIA Y QUÉ NO</h4>

            <div className="fila">
              <span className="crece">Pagos que te faltan</span>
              <b data-prueba="cancelar-plan-por-pagar">{prestamo.pagosQueFaltan} → cancelados</b>
            </div>
            <div className="fila">
              <span className="crece">Lo que debes</span>
              <b>{formatearImporte(prestamo.deudaVigente, prestamo.cuenta.codigoISO)} · no cambia</b>
            </div>
            <div className="fila">
              <span className="crece">Tus pagos registrados</span>
              <b>{prestamo.pagos.length} · no se tocan</b>
            </div>
            <div className="fila">
              <span className="crece">Los gastos de intereses</span>
              <b>Se quedan en sus meses</b>
            </div>

            <span className="ayuda">
              Deja de aparecer en lo que vence y el calendario y el conteo dejan de verse. Las filas
              canceladas se conservan, para que quede el rastro.
            </span>

            <div className="aviso w" role="status" data-prueba="cancelar-plan-no-se-deshace">
              ⚠ <b>No se puede deshacer.</b> Un plan cancelado no se reactiva: se genera de nuevo, y
              puedes hacerlo cuando quieras.
            </div>
          </div>

          <div className="card" style={{ margin: 12 }}>
            <div className="campo">
              <label htmlFor="motivo">MOTIVO (OPCIONAL)</label>
              <input
                id="motivo"
                name="motivo"
                type="text"
                maxLength={LARGO_MAXIMO_DEL_MOTIVO}
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
                data-prueba="cancelar-plan-campo-motivo"
              />
              <span className="ayuda">Queda guardado junto con la cancelación.</span>
            </div>

            {/* RN-753 / RN-754 · cuando no procede, el botón de confirmar NO EXISTE y
                la razón se dice: plan ya cancelado, plan de la otra persona, o sin
                plan que cancelar. Nada se escribe. */}
            {procede ? (
              <button
                className="btn ancho"
                type="button"
                disabled={cancelando}
                onClick={confirmar}
                data-prueba="cancelar-plan-confirmar"
              >
                Sí, cancelar el plan de pagos
              </button>
            ) : (
              <div className="aviso i" role="status" data-prueba="cancelar-plan-no-procede">
                {!prestamo.esPropio
                  ? 'Este préstamo es de la otra persona del hogar: su plan lo cancela quien es su dueño.'
                  : 'Este préstamo no tiene un plan de pagos vigente que cancelar.'}
              </div>
            )}

            <button
              className="btn sec ancho"
              type="button"
              onClick={() => navegar(`/cuentas/prestamos/${idCuenta}`, { replace: true })}
              data-prueba="cancelar-plan-volver"
            >
              No, dejarlo como está
            </button>
          </div>
        </>
      )}
    </Shell>
  );
}
